import { Grid, Box, Heading, Flex, SimpleGrid, Text, Icon } from '@chakra-ui/react'
import { useTranslation } from 'next-i18next/pages'
import { FormProvider, useForm } from 'react-hook-form'
import { VerifyFormButton } from '../atoms/VerifyFormButton'
import { VerifyFormInput } from '../atoms/VerifyFormInput'
import { VerifyFormTextarea } from '../atoms/VerifyFormTextarea'
import { EmptyPlaceholder } from '../molecules/EmptyPlaceholder'
import { VerifyFormTitle } from '../molecules/VerifyFormTitle'
import { useEffect, useState } from 'react'
import { MinePanel } from '@/components/organisms/MinesGamePlayground/MinesGamePlayground'
import ShurikenIcon from '@/public/assets/icons/shuriken.svg'
import { colors } from '@/theme'
import { getRoundNumbers, getUint32 } from '../pkg/rng_v2'
import { sha256 } from '@noble/hashes/sha256'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Section } from '../utils'

const fisherShuffle = (data: Array<any>, n: number, randomness: Array<number>) => {
  if (n > data.length) {
    throw new Error('attempt to pick more elements than the input length')
  }

  if (data.length != randomness.length) {
    throw new Error('attempt to pick more elements than the randomness length')
  }

  randomness.reverse()

  for (let i = data.length - 1; i > 0; i--) {
    let j = randomness[i]
    ;[data[i], data[j]] = [data[j], data[i]] //Swap elements
  }

  return data.slice(0, n)
}

const schema = yup
  .object({
    signature: yup
      .string()
      .required()
      .label('Signature')
      .test('is-hex', '${path} is not hex', (value) => /^(0x)?[0-9a-fA-F]+$/.test(value || '')),
    gameId: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .label('Game id')
      .min(1),
    round: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .label('Round id')
      .min(1),
    mines: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required()
      .min(1)
      .label('Mines count')
      .max(24),
  })
  .required()

export const MinesVerify = () => {
  const { t } = useTranslation()
  const form = useForm({
    resolver: yupResolver(schema),
  })
  const [state, setState] = useState<number[] | undefined>()
  const [openedTiles, setOpenedTiles] = useState(new Set<number>())

  useEffect(() => {
    const { unsubscribe } = form.watch(() => {
      setState(undefined)
    })
    return () => unsubscribe()
  }, [form.watch])

  useEffect(() => {
    setState(undefined)
  }, [openedTiles])

  const handleClick = async () => {
    await form.trigger()
    if (!form.formState.isValid) {
      setState(undefined)
      return
    }

    const signature = form.getValues('signature').replace('0x', '') as any

    const randomness = Buffer.from((await sha256(signature)) as any).toString('hex')

    const gameId = form.getValues('gameId')
    const roundId = form.getValues('round')

    const minesCount = Number(form.getValues('mines'))

    let opened_cells = [].concat(...(openedTiles as any)) as number[]

    let no_opened_cells = 25 - opened_cells.length

    let seed = `mines-${gameId}-${roundId}`

    const result = getRoundNumbers(
      await getUint32({
        serverKey: Buffer.from(randomness, 'hex'),
        userSeed: Buffer.from(seed, 'utf-8'),
        count: no_opened_cells,
      }),
      no_opened_cells,
      true
    )

    let difference = Array.from(Array(25).keys()).filter((x) => !opened_cells.includes(x))

    let outcome = fisherShuffle(difference, minesCount, result)

    setState(outcome)
  }

  return (
    <Grid gap={5} gridTemplateColumns={{ base: '1fr', xl: '3fr 2fr' }}>
      <Flex gap={5} p={5} flexDir={{ base: 'column', lg: 'row' }} rounded={'24px'} bg={'ninja.600'}>
        <FormProvider {...form}>
          <Box w={{ base: 'full', lg: '220px' }} flexShrink={0}>
            <VerifyFormTitle mode="drng" />
            <VerifyFormTextarea
              label={t('verify.signature')}
              placeholder={t('verify.enter-signature')}
              name={'signature'}
            />
            <VerifyFormInput
              label={t('verify.game-id')}
              placeholder={t('verify.enter-game-id')}
              name={'gameId'}
              type={'number'}
            />
            <VerifyFormInput
              label={t('verify.round')}
              placeholder={t('verify.enter-round')}
              name={'round'}
              type={'number'}
            />
            <VerifyFormInput
              label={t('verify.mines')}
              placeholder={t('verify.enter-mines')}
              name={'mines'}
              type={'number'}
            />
            <Box display={{ base: 'none', lg: 'block' }}>
              <VerifyFormButton onClick={handleClick} />
            </Box>
          </Box>
          <Box border={'1px solid'} borderColor={'ninja.500'} rounded={'24px'} p={5} w={'full'}>
            <Text mb={5} textAlign={'center'} color={'ninja.100'} fontSize={'sm'} fontWeight={700}>
              {t('verify.select-opened-tiles')}
            </Text>
            <SimpleGrid maxW={'405px'} mx={'auto'} gap={2} columns={5}>
              {Array.from({ length: 25 }, (_, index) => {
                const isActive = openedTiles.has(index)

                if (isActive) {
                  return (
                    <Box
                      cursor={'pointer'}
                      onClick={() =>
                        setOpenedTiles((prev) => {
                          const clone = new Set(prev)
                          clone.delete(index)

                          return clone
                        })
                      }
                    >
                      <MinePanel key={index} isMine={false} isActive={true} />
                    </Box>
                  )
                }

                return (
                  <Flex
                    key={index}
                    onClick={() =>
                      setOpenedTiles((prev) => {
                        const clone = new Set(prev)
                        clone.add(index)
                        return clone
                      })
                    }
                    color={isActive ? 'white' : 'ninja.400'}
                    bg={'ninja.800'}
                    rounded={'16px'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    aspectRatio={1}
                    cursor={'pointer'}
                    bgGradient={isActive ? colors.gradient.primary : 'none'}
                    _hover={{
                      color: isActive ? 'white' : 'ninja.200',
                    }}
                  >
                    <Icon w={'80%'} h={'80%'} as={ShurikenIcon} />
                  </Flex>
                )
              })}
            </SimpleGrid>
          </Box>
          <Box display={{ base: 'block', lg: 'none' }}>
            <VerifyFormButton onClick={handleClick} />
          </Box>
        </FormProvider>
      </Flex>
      <Box p={5} minH={'375px'} rounded={'24px'} bg={'ninja.600'}>
        <Heading fontSize={'xl'} mb={6}>
          {t('common.result')}
        </Heading>
        {!state ? (
          <EmptyPlaceholder game={Section.MINES} />
        ) : (
          <MinesVerifyResult openedTiles={openedTiles} result={state} />
        )}
      </Box>
    </Grid>
  )
}

const MinesVerifyResult = ({ result, openedTiles }: { result: number[]; openedTiles: Set<number> }) => {
  return (
    <SimpleGrid maxW={'450px'} mx={'auto'} columns={5} gap={2}>
      {Array.from({ length: 25 }, (_, index) => (
        <MinePanel key={index} isMine={result.includes(index)} isActive={openedTiles.has(index)} />
      ))}
    </SimpleGrid>
  )
}
